/**
 * AddDubSyncTrackDialog.js
 *
 * Modal for adding a DubSync track in the Video Editor.
 * Lets the user pick a language (must have an existing SRT/VTT) and optionally
 * upload a dubbed-audio MP3 if the language has no dubbingTransformedUrl yet.
 */

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { useTrackUsage } from '../../../hooks/useTrackUsage';

const UploadZone = styled.div`
    margin-top: 16px;
    border: 2px dashed ${({ active }) => (active ? '#64b5f6' : '#3a3a5a')};
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    background: ${({ active }) => (active ? 'rgba(100,181,246,0.08)' : 'transparent')};
    transition: border-color 0.2s, background 0.2s;
    color: #9090b0;
    font-size: 13px;
`;

const FileName = styled.span`
    display: block;
    margin-top: 8px;
    font-size: 12px;
    color: #64b5f6;
`;

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {function} props.onClose
 * @param {Array}    props.videoLanguages   — videoData.videoLanguages items
 * @param {string[]} props.existingDubSyncLanguages — languages already added as DubSync tracks
 * @param {function} props.onAdd  — ({ language, uploadedFile }) => void
 */
export default function AddDubSyncTrackDialog({
    open,
    onClose,
    videoLanguages = [],
    existingDubSyncLanguages = [],
    onAdd,
}) {
    useTrackUsage('AddDubSyncTrackDialog', 'dialog', 'videoeditor', open);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Only show languages that have an SRT or VTT (needed for alignment)
    const eligibleLanguages = videoLanguages.filter(
        (vl) =>
            (vl.vttFileUrl || vl.srtFileUrl) &&
            !vl.languageName.endsWith('-DubSync') &&
            !existingDubSyncLanguages.includes(vl.languageName),
    );

    const selectedVL = eligibleLanguages.find((vl) => vl.languageName === selectedLanguage);
    const hasDubbedAudio = Boolean(selectedVL?.dubbingTransformedUrl);

    // Check if a DubSync transcription already exists for the selected language.
    // If it does, the user can proceed directly to align without re-uploading audio.
    const existingDubSyncVL = selectedLanguage
        ? videoLanguages.find((vl) => vl.languageName === `${selectedLanguage}-DubSync`)
        : null;
    const transcriptionAlreadyDone = Boolean(
        existingDubSyncVL &&
        ((existingDubSyncVL.srtFileUrl && existingDubSyncVL.srtFileUrl.trim()) ||
         (existingDubSyncVL.vttFileUrl && existingDubSyncVL.vttFileUrl.trim())),
    );

    const handleLanguageChange = (e) => {
        setSelectedLanguage(e.target.value);
        setUploadedFile(null);
    };

    const handleFileSelect = (file) => {
        if (!file) return;
        const isAudioMime = file.type && file.type.startsWith('audio/');
        const isAcceptExt = /\.(mp3|wav)$/i.test(file.name || '');
        if (isAudioMime || isAcceptExt) {
            setUploadedFile(file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        handleFileSelect(file);
    };

    const handleAdd = () => {
        if (!selectedLanguage) return;
        if (!hasDubbedAudio && !uploadedFile && !transcriptionAlreadyDone) return;
        onAdd({ language: selectedLanguage, uploadedFile: hasDubbedAudio ? null : uploadedFile, transcriptionAlreadyDone });
        handleClose();
    };

    const handleClose = () => {
        setSelectedLanguage('');
        setUploadedFile(null);
        onClose();
    };

    const canAdd = selectedLanguage && (hasDubbedAudio || uploadedFile || transcriptionAlreadyDone);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    background: '#12122a',
                    color: '#e0e0ff',
                    border: '1px solid #2a2a4a',
                },
            }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid #2a2a4a', pb: 1.5 }}>
                🎙 Add DubSync Track
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel sx={{ color: '#9090b0' }}>Language</InputLabel>
                    <Select
                        value={selectedLanguage}
                        label="Language"
                        onChange={handleLanguageChange}
                        sx={{
                            color: '#e0e0ff',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a3a5a' },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    background: '#1a1a2e',
                                    color: '#e0e0ff',
                                    border: '1px solid #2a2a4a',
                                },
                            },
                        }}
                    >
                        {eligibleLanguages.length === 0 && (
                            <MenuItem disabled value="">
                                No eligible languages — upload an SRT first
                            </MenuItem>
                        )}
                        {eligibleLanguages.map((vl) => (
                            <MenuItem key={vl.languageName} value={vl.languageName}>
                                {vl.languageName}
                                {vl.dubbingTransformedUrl ? ' ✓ dubbed audio' : ' — needs audio upload'}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText sx={{ color: '#7070a0' }}>
                        Only languages with an existing SRT/VTT are shown
                    </FormHelperText>
                </FormControl>

                {selectedLanguage && !hasDubbedAudio && !transcriptionAlreadyDone && (
                    <>
                        <Typography variant="body2" sx={{ mt: 2, color: '#9090c0' }}>
                            No dubbed audio found for <strong style={{ color: '#e0e0ff' }}>{selectedLanguage}</strong>.
                            Upload a dubbed audio file (MP3 or WAV) to continue.
                        </Typography>
                        <UploadZone
                            active={dragOver ? 1 : 0}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {uploadedFile
                                ? <>
                                    ✅ Ready to upload
                                    <FileName>{uploadedFile.name}</FileName>
                                  </>
                                : 'Drop .mp3 or .wav here or click to browse'
                            }
                        </UploadZone>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="audio/*,.mp3,.wav"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileSelect(e.target.files?.[0])}
                        />
                    </>
                )}

                {selectedLanguage && transcriptionAlreadyDone && (
                    <Typography variant="body2" sx={{ mt: 2, color: '#81c784' }}>
                        ✓ Transcription already complete — click Add to go straight to alignment.
                    </Typography>
                )}

                {selectedLanguage && hasDubbedAudio && !transcriptionAlreadyDone && (
                    <Typography variant="body2" sx={{ mt: 2, color: '#81c784' }}>
                        ✓ Dubbed audio is ready. Click Add to create the DubSync track.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid #2a2a4a', pr: 2, pb: 1.5 }}>
                <Button onClick={handleClose} sx={{ color: '#9090b0' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!canAdd}
                    onClick={handleAdd}
                    sx={{
                        background: canAdd ? 'linear-gradient(135deg,#3a7bd5,#8e44ad)' : undefined,
                    }}
                >
                    Add Track
                </Button>
            </DialogActions>
        </Dialog>
    );
}
